import { SystemsType, useInstallationsScoreQuery } from '@/__generated__/types';
import type { DashboardPeriod } from '@/constants/dashboard-period';

import { UNRESOLVED_SCOPE } from '../constants';
import type { SpaceScope } from '../types/home-types';

export interface UseInstallationsScoreParams {
  scope?: SpaceScope;
  timeSpan: DashboardPeriod;
}

const SYSTEM_TYPES = Object.values(SystemsType).filter(
  (type) => type !== SystemsType.Unknown,
);

/** Upper bound of the LOW score band (insights-app INSTALLATION_SCORES). */
export const LOW_SCORE_MAX = 50;

export const useInstallationsScore = ({
  scope,
  timeSpan,
}: UseInstallationsScoreParams) => {
  const { data, loading, error, refetch } = useInstallationsScoreQuery({
    skip: !scope,
    variables: {
      ...(scope ?? UNRESOLVED_SCOPE),
      systemTypes: SYSTEM_TYPES,
      dataFilters: { timeSpan, readUntilNow: true },
    },
    errorPolicy: 'all',
  });

  const scores = (data?.systemsOrSubsystemsScoreBySpace ?? [])
    .map((item) => item.score)
    .filter((score): score is number => score != null);
  const total = scores.length;
  const underperforming = scores.filter(
    (score) => score <= LOW_SCORE_MAX,
  ).length;

  return {
    total,
    underperforming,
    percent: total === 0 ? 0 : Math.round((100 * underperforming) / total),
    isLoading: loading || !scope,
    isError: !!error,
    isEmpty: !loading && total === 0,
    retry: () => {
      void refetch();
    },
  };
};
